import { useNavigate } from "react-router-dom";
import { TipBox, TipWrap, useTip } from "./Tip";

export type DayDatum = { day: string; positive: number; neutral: number; negative: number };

const nf = (x: number) => Intl.NumberFormat("id-ID").format(x ?? 0);
const wibDay = (d: string) => d.slice(8) + "/" + d.slice(5, 7);

/** Kolom harian bertumpuk (positif/netral/negatif).
 *  Mark tipis, jeda 2px antar segmen, ujung atas membulat 4px, tanpa gridline berat.
 *  Hover = tooltip berisi angka; klik = drill ke daftar hari itu.
 *
 *  Dulu tooltipnya murni CSS (`opacity` pada `:hover`). Itu bekerja untuk mouse,
 *  tetapi tidak pernah muncul pada fokus keyboard dan tampil berbeda dari tooltip
 *  grafik lain di halaman yang sama. Sekarang memakai primitif yang sama. */
export function DayCols({ data, hrefOf, height = 190 }: { data: DayDatum[]; hrefOf?: (d: DayDatum) => string; height?: number }) {
  const nav = useNavigate();
  const { wrapRef, tip, tipUntuk } = useTip();
  if (!data.length) {
    return <div className="avio-empty"><i className="fa-regular fa-chart-bar avio-empty__icon" /><div className="avio-empty__title">Belum ada data pada rentang ini.</div></div>;
  }
  const tot = (d: DayDatum) => d.positive + d.neutral + d.negative;
  const max = Math.max(1, ...data.map(tot));
  const peak = data.reduce((a, b) => (tot(b) > tot(a) ? b : a), data[0]);
  return (
    <TipWrap wrapRef={wrapRef}>
      <div className="avio-cols" style={{ height }}>
        {data.map((d) => {
          const t = tot(d);
          const hPct = (t / max) * 100;
          const seg = (v: number) => (t ? (v / t) * 100 : 0);
          const isi = {
            judul: wibDay(d.day),
            baris: [
              { label: "total", nilai: nf(t) },
              { warna: "var(--mark-pos)", label: "positif", nilai: nf(d.positive) },
              { warna: "var(--mark-neu)", label: "netral", nilai: nf(d.neutral) },
              { warna: "var(--mark-neg)", label: "negatif", nilai: nf(d.negative) },
            ],
          };
          return (
            <div key={d.day} className="avio-col" data-peak={d.day === peak.day ? "true" : undefined}
              style={{ cursor: hrefOf ? "pointer" : "default" }}
              onClick={() => hrefOf && nav(hrefOf(d))}
              onKeyDown={(e) => { if (hrefOf && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); nav(hrefOf(d)); } }}
              role={hrefOf ? "button" : undefined}
              aria-label={`${d.day}: ${nf(t)} total, positif ${nf(d.positive)}, netral ${nf(d.neutral)}, negatif ${nf(d.negative)}`}
              {...tipUntuk(isi)}>
              <div style={{ height: `${hPct}%`, display: "flex", flexDirection: "column", gap: 2, justifyContent: "flex-end", position: "relative" }}>
                {d.day === peak.day && <span className="avio-peak">{nf(t)}</span>}
                <div className="avio-col__seg" style={{ height: `${seg(d.negative)}%`, background: "var(--mark-neg)" }} />
                <div className="avio-col__seg" style={{ height: `${seg(d.neutral)}%`, background: "var(--mark-neu)" }} />
                <div className="avio-col__seg" style={{ height: `${seg(d.positive)}%`, background: "var(--mark-pos)" }} />
              </div>
            </div>
          );
        })}
      </div>
      <div className="avio-axis">
        <span>{wibDay(data[0].day)}</span>
        <span>puncak {wibDay(peak.day)} · {nf(tot(peak))}</span>
        <span>{wibDay(data[data.length - 1].day)}</span>
      </div>
      <TipBox tip={tip} />
    </TipWrap>
  );
}
